import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { createPasswordReset } from '../lib/invitationService';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const { login, isLoading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('LoginPage: Form submitted');
    setError('');
    
    console.log('LoginPage: Email:', email);
    console.log('LoginPage: Password length:', password.length);
    
    if (!email || !password) {
      console.log('LoginPage: Missing email or password');
      setError('Please enter both email and password');
      return;
    }
    
    console.log('LoginPage: Calling login function...');
    const success = await login(email, password);
    console.log('LoginPage: Login result:', success);
    
    if (!success) {
      setError('Invalid credentials. Please try again.');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');

    if (!resetEmail) {
      setResetError('Please enter your email address.');
      return;
    }

    setIsResetting(true);
    try {
      const result = await createPasswordReset(resetEmail, '', '');
      if (result) {
        setResetMessage(
          `Your password has been reset.\n\nYour new temporary password is:\n${result.tempPassword}\n\nPlease copy it now and use it to log in. You will be asked to set a new password.`
        );
      } else {
        setResetError('Unable to reset password. Please contact an administrator.');
      }
    } catch (err: any) {
      if (err?.message === 'User not found') {
        setResetError('No account found with that email address.');
      } else {
        setResetError('Unable to reset password. Please contact an administrator.');
      }
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img 
            src="/fanexpo-chicago-logo.png" 
            alt="Fan Expo Chicago" 
            className="h-12 sm:h-16 w-auto"
          />
        </div>
        <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
          Press Center Login
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 px-2">
          FAN EXPO Chicago Press Management
        </p>
      </div>

      <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 px-4 shadow sm:rounded-lg sm:px-10 sm:py-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm touch-manipulation"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm touch-manipulation"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 touch-manipulation"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setResetEmail('');
                setResetMessage('');
                setResetError('');
              }}
              className="text-sm text-indigo-600 hover:text-indigo-500"
            >
              Forgot your password?
            </button>
          </div>

        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
              <button
                onClick={() => setShowForgotPassword(false)}
                className="text-gray-400 hover:text-gray-500 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            {resetMessage ? (
              <div>
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                  <pre className="text-sm text-green-800 whitespace-pre-wrap font-sans">{resetMessage}</pre>
                </div>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetMessage('');
                  }}
                  className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <p className="text-sm text-gray-600 mb-4">
                  Enter the email address associated with your account and we'll generate a new temporary password for you.
                </p>
                <div className="mb-4">
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm touch-manipulation"
                    placeholder="Enter your email"
                  />
                </div>

                {resetError && (
                  <div className="text-red-600 text-sm mb-4">{resetError}</div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isResetting}
                    className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isResetting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Powered by Acacia footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-400">
          Powered by{' '}
          <a 
            href="https://teamacacia.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-indigo-500 hover:text-indigo-600 font-medium"
          >
            Acacia
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;